import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import LinkIcon from '@mui/icons-material/Link';
import {
  getMenuItemNamesColor,
  getButtonBackgroundHoverColor,
  getHeaderAndNavigationTypography
} from 'styles/helpers/getThemeStyles';
import {
  INVITE_HEADER,
  START_ORDERING,
  getInviteHelperMaxGuests
} from 'bento-ordering/group_ordering/constants/groupOrder';
import ModalCloseButton from 'bento-ordering/base/ui_components/ModalCloseButton';
import { goToMenu } from 'bento-ordering/cart/actions/orderSettings';
import { MEDIUM_TOFU, SOY } from 'styles/theme/colors';
import PrimaryCta from 'components/Buttons/PrimaryCta';
import {
  HOUR_MINUTE_DAY_TIME,
  ABBREVIATED_MONTH
} from 'bento-ordering/base/constants/timeFormat';
import moment from 'moment';
import ShareButton from 'bento-ordering/group_ordering/components/ShareButton';
import { useIsMobile } from 'styles/hooks/mediaQueries';
import {
  selectCartGroupOrder,
  selectGroupOrderingMaxDiners
} from 'bento-ordering/cart/selectors/index';
import { StyledSnackbar } from 'bento-ordering/group_ordering/ui_components/GroupOrder';

const StyledGroupOrderInviteWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.card.background.main,
  margin: '20px 16px',
  height: '100%'
}));

const TitleWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  margin: '.5rem'
});

const ButtonsContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-around',
  paddingBottom: '48%'
});

const LinkContainer = styled(Box)({
  display: 'flex',
  margin: '1rem',
  marginTop: '1rem'
});

const ShareContainer = styled(Box)({
  display: 'flex',
  margin: '1rem',
  marginRight: '2rem',
  marginTop: '1rem'
});

const QrCodeContainer = styled(Box)({
  textAlign: 'center',
  margin: '0 auto',
  borderBottom: `1px solid`,
  borderColor: MEDIUM_TOFU
});

const QRCode = styled('img')({
  height: '200px',
  width: '200px'
});

const QRCodeTypography = styled(Typography)({
  color: SOY,
  fontWeight: '500',
  fontSize: '16px',
  lineHeight: 1,
  marginBottom: '16px'
});

const TitleTypography = styled(Typography)(({ theme }) => ({
  color: getMenuItemNamesColor(theme),
  fontSize: '20px',
  fontWeight: '500'
}));

const HelperTypography = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  margin: '0 0 1rem .5rem',
  fontColor: getButtonBackgroundHoverColor(theme),
  ...getHeaderAndNavigationTypography(theme),
  cursor: 'pointer'
}));

const ButtonsTypography = styled(Typography)({
  color: SOY,
  fontWeight: '500',
  fontSize: '16px',
  cursor: 'pointer'
});

const LineBreak = styled('div')({
  border: '.25px solid',
  borderColor: MEDIUM_TOFU,
  marginTop: '.5rem',
  marginBottom: '.5rem'
});

const GroupOrderInvite = () => {
  const dispatch = useDispatch();
  const {
    admin_name: adminName,
    short_url: shortUrl,
    spending_limit: spendingLimit,
    deadline,
    qr_code: qrCodeUrl
  } = useSelector(selectCartGroupOrder);
  const restaurantName = useSelector(state => state.account.name);
  const maxGuests = useSelector(selectGroupOrderingMaxDiners);
  const isMobile = useIsMobile();
  const [isSnackbarOpen, setSnackbarOpen] = useState(false);

  const generateLinkMessage = () => {
    let primary = `🙌 Hey! Come join ${adminName}'s group order at ${restaurantName} ${shortUrl}`;
    if (spendingLimit) {
      primary = primary + `\n$${spendingLimit} Spending limit per person`;
    }
    if (deadline) {
      const dateDeadline = moment(deadline);
      primary =
        primary +
        `\nDue by ${dateDeadline.format(
          ABBREVIATED_MONTH
        )}, ${deadline.getDate()} ${deadline.format(HOUR_MINUTE_DAY_TIME)}}`;
    }
    primary = primary + '\nEnjoy!';
    return primary;
  };

  const linkMessage = generateLinkMessage();

  const handleCopy = () => {
    navigator.clipboard.writeText(shortUrl);
    setSnackbarOpen(true);
  };

  const isShareAvailable = navigator.canShare;

  return (
    <>
      <ModalCloseButton autoFocus handleClick={() => dispatch(goToMenu())} />
      <StyledGroupOrderInviteWrapper>
        <TitleWrapper>
          <TitleTypography>{INVITE_HEADER}</TitleTypography>
        </TitleWrapper>
        <HelperTypography>
          {getInviteHelperMaxGuests(maxGuests)}
        </HelperTypography>
        <QrCodeContainer>
          <QRCode src={qrCodeUrl} alt="QR Code" />
          <QRCodeTypography>SCAN THE QR CODE</QRCodeTypography>
        </QrCodeContainer>
        <ButtonsContainer>
          <LinkContainer>
            <LinkIcon sx={{ fill: SOY }} />
            <ButtonsTypography variant="div" onClick={handleCopy}>
              Copy Link
            </ButtonsTypography>
          </LinkContainer>
          {isMobile && isShareAvailable && (
            <>
              <LineBreak />
              <ShareContainer>
                <ShareButton text={linkMessage} />
              </ShareContainer>
            </>
          )}
        </ButtonsContainer>
        <PrimaryCta
          handleClick={() => dispatch(goToMenu())}
          type="button"
          fullWidth
          label={START_ORDERING}
        />
        <StyledSnackbar
          open={isSnackbarOpen}
          autoHideDuration={5000}
          message="Link Copied"
          onClose={() => setSnackbarOpen(false)}
        />
      </StyledGroupOrderInviteWrapper>
    </>
  );
};

export default GroupOrderInvite;
